/**
 * Core Walkthrough Hook
 *
 * Wraps Intro.js and handles:
 * - Filtering steps by platform (desktop/mobile)
 * - Filtering steps by role (owner/admin/rep)
 * - Removing steps for elements not in the DOM
 * - Managing Intro.js lifecycle
 * - Firestore tracking on completion/skip
 *
 * CRM adaptation: Uses useAuth() role checks instead of usePermissions().
 */

import { useCallback, useRef, useEffect } from 'react';
import introJs from 'intro.js';
import 'intro.js/introjs.css';
import './introjs-custom.css';
import type { TourConfig, TourStep } from './types';
import { useAuth } from '@/contexts/AuthContext';

/** Tailwind lg: breakpoint -- below this is "mobile" */
const MOBILE_BREAKPOINT = 1024;

interface UseWalkthroughOptions {
  /** Callback when tour completes (all steps seen) */
  onComplete?: () => void;
  /** Callback when tour is exited early */
  onExit?: () => void;
}

interface UseWalkthroughResult {
  /** Start the tour */
  startTour: () => void;
  /** Whether a tour is currently running */
  isRunning: boolean;
}

export function useWalkthrough(
  config: TourConfig | null,
  options?: UseWalkthroughOptions
): UseWalkthroughResult {
  const introRef = useRef<ReturnType<typeof introJs> | null>(null);
  const isRunningRef = useRef(false);
  const { user } = useAuth();

  // Derive role string from the auth user object
  const userRole = user?.role ?? null;

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (introRef.current) {
        introRef.current.exit(true);
        introRef.current = null;
      }
    };
  }, []);

  const filterSteps = useCallback(
    (steps: TourStep[]): TourStep[] => {
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

      return steps.filter((step) => {
        // Platform filter
        if (step.platform !== 'both') {
          if (isMobile && step.platform !== 'mobile') return false;
          if (!isMobile && step.platform !== 'desktop') return false;
        }

        // Role filter -- skip if user's role is not in the list
        if (step.requiredRoles?.length) {
          if (!userRole) return false;
          if (!step.requiredRoles.includes(userRole)) return false;
        }

        // CRM has no permission matrix, so requiredPermissions is ignored

        // DOM check -- skip if the target element doesn't exist
        if (step.element && !document.querySelector(step.element)) return false;

        return true;
      });
    },
    [userRole]
  );

  const startTour = useCallback(() => {
    if (!config || isRunningRef.current) return;

    const filtered = filterSteps(config.steps);
    if (filtered.length === 0) return;

    // Build Intro.js steps
    const introSteps = filtered.map((step) => ({
      element: step.element ? (document.querySelector(step.element) as HTMLElement) : undefined,
      title: step.title,
      intro: step.intro,
      position: (step.position || 'auto') as 'top' | 'left' | 'bottom' | 'right' | 'floating',
    }));

    const intro = introJs();
    introRef.current = intro;
    isRunningRef.current = true;

    intro.setOptions({
      steps: introSteps,
      showProgress: true,
      showBullets: false,
      showStepNumbers: false,
      exitOnOverlayClick: false,
      disableInteraction: false,
      scrollToElement: true,
      scrollPadding: 80,
      overlayOpacity: 0.5,
      nextLabel: 'Next &rarr;',
      prevLabel: '&larr; Back',
      doneLabel: 'Done',
      tooltipClass: 'walkthrough-tooltip',
      highlightClass: 'walkthrough-highlight',
    });

    // Handle onBeforeChange callbacks (e.g., tab switching)
    intro.onbeforechange(async function (this: ReturnType<typeof introJs>) {
      const currentStep = (this as unknown as { _currentStep: number })._currentStep;
      const step = filtered[currentStep];
      if (step?.onBeforeChange) {
        await step.onBeforeChange();
        // Wait for DOM to update after tab switch
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
      return true;
    });

    intro.oncomplete(() => {
      isRunningRef.current = false;
      introRef.current = null;
      options?.onComplete?.();
    });

    intro.onexit(() => {
      isRunningRef.current = false;
      introRef.current = null;
      options?.onExit?.();
    });

    // Small delay to ensure DOM is ready
    requestAnimationFrame(() => {
      intro.start();
    });
  }, [config, filterSteps, options]);

  return {
    startTour,
    isRunning: isRunningRef.current,
  };
}
